(function () {
  class XTSFaq extends HTMLElement {
    constructor() {
      super();
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.onSummaryPreload = this.onSummaryPreload.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.animations = new WeakMap();
      this.preloadedBackgrounds = new Set();
      this.preloadQueue = [];
    }

    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      this.faqItems = Array.from(this.querySelectorAll('[data-faq-item]'));
      if (this.faqItems.length < 1) return;

      this.faqItems.forEach((item) => {
        const summary = this.getSummary(item);
        if (summary) {
          summary.addEventListener('click', this.onSummaryClick);
          summary.addEventListener('pointerenter', this.onSummaryPreload, { passive: true });
          summary.addEventListener('focus', this.onSummaryPreload);
        }
      });
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.shouldClosePrevious()) {
        this.keepSingleOpenedItem();
      }
      this.updateBackgroundFromActiveItem();
      this.setupBackgroundPreload();
    }

    disconnectedCallback() {
      if (!this.isInitialized || !this.faqItems) return;

      this.faqItems.forEach((item) => {
        const summary = this.getSummary(item);
        if (summary) {
          summary.removeEventListener('click', this.onSummaryClick);
          summary.removeEventListener('pointerenter', this.onSummaryPreload);
          summary.removeEventListener('focus', this.onSummaryPreload);
        }
        this.cancelAnimation(item);
        this.cleanupItemStyles(item);
      });
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.cleanupBackgroundPreload();
      this.resetBackground();
    }

    shouldClosePrevious() {
      return this.dataset.closePrevious === 'true';
    }

    getDuration() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
      return 250;
    }

    getSummary(item) {
      return item.querySelector('summary');
    }

    getContent(item) {
      return this.getSummary(item)?.nextElementSibling;
    }

    getActiveItem() {
      return this.faqItems.find((item) => item.open) || null;
    }

    formatBackgroundUrl(url) {
      if (!url) return '';
      return `url("${url.replace(/"/g, '\\"')}")`;
    }

    getCurrentBackgroundUrl(item) {
      if (!item) return '';
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (isMobile) {
        return item.dataset.backgroundMobile || item.dataset.backgroundDesktop || '';
      }

      return item.dataset.backgroundDesktop || item.dataset.backgroundMobile || '';
    }

    preloadBackgroundUrl(url) {
      if (!url || this.preloadedBackgrounds.has(url)) return;

      this.preloadedBackgrounds.add(url);
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      image.src = url;
    }

    onSummaryPreload(event) {
      const item = event.currentTarget.closest('[data-faq-item]');
      this.preloadBackgroundUrl(this.getCurrentBackgroundUrl(item));
    }

    setupBackgroundPreload() {
      if (!('IntersectionObserver' in window)) {
        this.queueBackgroundPreloads();
        return;
      }

      this.preloadObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.cleanupBackgroundPreload();
          this.queueBackgroundPreloads();
        },
        { rootMargin: '300px 0px' }
      );

      this.preloadObserver.observe(this);
    }

    cleanupBackgroundPreload() {
      if (!this.preloadObserver) return;
      this.preloadObserver.disconnect();
      this.preloadObserver = null;
    }

    queueBackgroundPreloads() {
      const urls = this.faqItems
        .map((item) => this.getCurrentBackgroundUrl(item))
        .filter((url, index, allUrls) => url && allUrls.indexOf(url) === index);

      this.preloadQueue = urls.filter((url) => !this.preloadedBackgrounds.has(url));
      this.preloadNextBackground();
    }

    preloadNextBackground() {
      const url = this.preloadQueue.shift();
      if (!url) return;

      const load = () => {
        this.preloadBackgroundUrl(url);

        if (this.preloadQueue.length > 0) {
          window.setTimeout(() => this.preloadNextBackground(), 250);
        }
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(load, { timeout: 1500 });
        return;
      }

      window.setTimeout(load, 500);
    }

    resetBackground() {
      this.style.setProperty('--xts-faq-active-background-desktop', 'var(--xts-faq-background-image-desktop)');
      this.style.setProperty('--xts-faq-active-background-mobile', 'var(--xts-faq-background-image-mobile)');
    }

    setBackgroundForItem(item) {
      const desktopUrl = item?.dataset.backgroundDesktop || item?.dataset.backgroundMobile || '';
      const mobileUrl = item?.dataset.backgroundMobile || item?.dataset.backgroundDesktop || '';

      if (!desktopUrl && !mobileUrl) {
        this.resetBackground();
        return;
      }

      this.style.setProperty(
        '--xts-faq-active-background-desktop',
        this.formatBackgroundUrl(desktopUrl)
      );
      this.style.setProperty(
        '--xts-faq-active-background-mobile',
        this.formatBackgroundUrl(mobileUrl)
      );
    }

    updateBackgroundFromActiveItem() {
      const activeItem = this.getActiveItem();
      if (activeItem) {
        this.setBackgroundForItem(activeItem);
        return;
      }

      this.resetBackground();
    }

    keepSingleOpenedItem(activeItem = null) {
      let openedItem = activeItem;
      if (!openedItem) {
        openedItem = this.faqItems.find((item) => item.open) || null;
      }

      this.faqItems.forEach((item) => {
        if (item === openedItem) return;
        if (!item.open) return;
        item.open = false;
        this.syncAria(item, false);
      });
    }

    syncAria(item, expanded) {
      const summary = this.getSummary(item);
      if (!summary) return;
      summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    cleanupItemStyles(item) {
      item.style.height = '';
      item.style.overflow = '';
    }

    cancelAnimation(item) {
      const animation = this.animations.get(item);
      if (!animation) return;
      animation.cancel();
      this.animations.delete(item);
    }

    animateItem(item, startHeight, endHeight, onFinish) {
      const duration = this.getDuration();

      this.cancelAnimation(item);
      item.style.height = startHeight;
      item.style.overflow = 'hidden';

      if (duration === 0) {
        item.style.height = endHeight;
        onFinish();
        return;
      }

      const animation = item.animate(
        {
          height: [startHeight, endHeight],
        },
        {
          duration,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }
      );

      this.animations.set(item, animation);

      animation.onfinish = () => {
        this.animations.delete(item);
        onFinish();
      };

      animation.oncancel = () => {
        this.animations.delete(item);
      };
    }

    openItem(item, animate = true) {
      const summary = this.getSummary(item);
      const content = this.getContent(item);
      if (!summary || !content) return;

      const startHeight = `${item.offsetHeight || summary.offsetHeight}px`;

      item.open = true;
      this.syncAria(item, true);
      this.setBackgroundForItem(item);

      const endHeight = `${summary.offsetHeight + content.scrollHeight}px`;

      if (!animate) {
        this.cancelAnimation(item);
        this.cleanupItemStyles(item);
        return;
      }

      this.animateItem(item, startHeight, endHeight, () => {
        this.cleanupItemStyles(item);
      });
    }

    closeItem(item, animate = true, updateBackground = true) {
      if (!item.open) return;

      const summary = this.getSummary(item);
      if (!summary) return;

      const startHeight = `${item.offsetHeight}px`;
      const endHeight = `${summary.offsetHeight}px`;

      if (!animate) {
        this.cancelAnimation(item);
        item.open = false;
        this.syncAria(item, false);
        this.cleanupItemStyles(item);
        if (updateBackground) this.updateBackgroundFromActiveItem();
        return;
      }

      this.animateItem(item, startHeight, endHeight, () => {
        item.open = false;
        this.syncAria(item, false);
        this.cleanupItemStyles(item);
        if (updateBackground) this.updateBackgroundFromActiveItem();
      });
    }

    closeOtherItems(activeItem, animate = true) {
      this.faqItems.forEach((item) => {
        if (item === activeItem) return;
        this.closeItem(item, animate, false);
      });
    }

    onSummaryClick(event) {
      event.preventDefault();

      const item = event.currentTarget.closest('[data-faq-item]');
      if (!item) return;

      const isOpen = item.open;
      if (isOpen) {
        this.closeItem(item, true);
        return;
      }

      if (this.shouldClosePrevious()) {
        this.closeOtherItems(item, false);
      }

      this.openItem(item, true);
    }

    onBlockSelect(event) {
      const selectedBlock = event.target.closest('[data-faq-block]');
      if (!selectedBlock || !this.contains(selectedBlock)) return;

      const selectedItem = selectedBlock.querySelector('[data-faq-item]');
      if (!selectedItem) return;

      if (this.shouldClosePrevious()) {
        this.closeOtherItems(selectedItem, false);
      }
      this.openItem(selectedItem, true);

      const summary = selectedItem.querySelector('summary');
      if (summary) summary.focus();
    }
  }

  if (!customElements.get('xts-faq')) {
    customElements.define('xts-faq', XTSFaq);
  }
})();
